/**
 * CategoryGroupDetail Component
 * 
 * Shows detailed expense/income graph for a specific category, group, or account
 * with interval selection similar to Analytics page.
 */

import { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback, ReactElement } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Transaction, CustomFieldAttachmentValue, TransactionCustomFieldScopeType } from "../types";
import { fieldAppliesToScope } from "../utils/transactionCustomFieldsDerivation";
import { useAnalyticsTransactions, TransactionWithDate } from "./Analytics/hooks/useAnalyticsTransactions";
import { useAnalyticsAccounts } from "./Analytics/hooks/useAnalyticsAccounts";
import { useAnalyticsWorkspace } from "./Analytics/hooks/useAnalyticsWorkspace";
import { useAnalyticsCategories } from "./Analytics/hooks/useAnalyticsCategories";
import { useAnalyticsCurrencies } from "./Analytics/hooks/useAnalyticsCurrencies";
import { useAnalyticsWorkspaceMembers } from "./Analytics/hooks/useAnalyticsWorkspaceMembers";
import { useAnalyticsHelpers, getCategoryColor } from "./Analytics/hooks/useAnalyticsHelpers";
import { getCurrencyDecimalPlaces } from "../utils/currencyDefaults";
import { useAnalyticsHandlers } from "./Analytics/hooks/useAnalyticsHandlers";
import { useAnalyticsUIState } from "./Analytics/hooks/useAnalyticsUIState";
import { AnalyticsCategoryList } from "./Analytics/components/AnalyticsCategoryList";
import DateRangePicker from "./Activity/components/DateRangePicker";
import { useActivityHelpers } from "./Activity/hooks/useActivityHelpers";
import { useCurrencyHelpers } from "./Activity/hooks/useCurrencyHelpers";
import { formatCurrency, copyToClipboard } from "../utils/transactionUtils";
import TransactionMenu from "./Activity/components/TransactionMenu";
import AttachmentPreviewDialog from "./Activity/components/AttachmentPreviewDialog";
import TransactionHistoryDialog from "./Activity/components/TransactionHistoryDialog";
import TransactionEditDialogStandalone from "./Activity/components/TransactionEditDialogStandalone";
import { useTransactionHistory } from "./Activity/hooks/useTransactionHistory";
import { useWorkspaceData } from "./Activity/hooks/useWorkspaceData";
import { usePermissionHelpers } from "./Activity/hooks/usePermissionHelpers";
import { useWorkspaceSettings } from "./WorkspaceSettings/hooks/useWorkspaceSettings";
import { useTransactionCustomFieldsSettings } from "./TransactionFieldsSettings/hooks/useTransactionCustomFieldsSettings";
import { api } from "../services/api";
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemText,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Avatar,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LinkIcon from "@mui/icons-material/Link";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TodayIcon from "@mui/icons-material/Today";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  LabelList,
  Label,
} from "recharts";

interface CategoryGroupDetailProps {
  workspaceId: string;
  /** Route segment: "group" | "category" | "account" */
  type?: string;
  /** Name of the group, category, or account (from URL segment) */
  name?: string;
  /** When provided, enables the filter button to open Activity with filters applied (same as Analytics list) */
  onNavigateToActivity?: (filters?: {
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  }) => void;
}

function CategoryGroupDetail({ workspaceId, type: typeProp, name: nameProp, onNavigateToActivity }: CategoryGroupDetailProps): ReactElement {
  const params = useParams<{ type: string; name: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = typeProp ?? params.type;
  const name = nameProp ?? params.name;
  const decodedName = name ? decodeURIComponent(name) : "";
  
  // Get period and date range from URL query parameters
  const urlPeriod = searchParams.get("period");
  const urlFromDate = searchParams.get("fromDate");
  const urlToDate = searchParams.get("toDate");
  /** "expenses" | "incomes" from URL - ensures correct label/color when opening from Analytics tab */
  const urlTab = searchParams.get("tab") === "incomes" ? "incomes" : searchParams.get("tab") === "expenses" ? "expenses" : null;

  // Prevent rapid clicks on navigation buttons
  const navigationCooldownRef = useRef<number>(0);
  const NAVIGATION_COOLDOWN_MS = 300; // 300ms cooldown between navigations
  
  // Track navigation direction to determine which arrow to disable when no data
  const lastNavigationDirectionRef = useRef<"left" | "right" | null>(null);
  
  // Track if we've initialized from URL to prevent re-initialization
  const initializedFromUrlRef = useRef<boolean>(false);
  // Ref for chart scroll container
  const chartScrollRef = useRef<HTMLDivElement>(null);

  // Data fetching hooks
  const { transactions, loading: transactionsLoading } = useAnalyticsTransactions({ workspaceId });
  const { accounts } = useAnalyticsAccounts({ workspaceId });
  const { workspaceCreatedAt, defaultCurrency, defaultCurrencyLoaded } = useAnalyticsWorkspace({ workspaceId });
  const { categories, groups, loading: categoriesLoading } = useAnalyticsCategories({ workspaceId });
  const { currencies } = useAnalyticsCurrencies({ workspaceId, defaultCurrency });
  const { workspaceMembers } = useAnalyticsWorkspaceMembers({ workspaceId });
  const { workspaceOwnerId, permissions, currentUserRole } = useWorkspaceData({ workspaceId });
  const { timeFormat, dateFormat } = useWorkspaceSettings({ workspaceId });
  const { canEditTransactions, canDeleteTransactions, canViewTransactionHistory } = usePermissionHelpers({
    permissions,
    currentUserRole,
    workspaceOwnerId,
  });
  const { fields: transactionCustomFields } = useTransactionCustomFieldsSettings({ workspaceId });
  const expenseCategories = useMemo(() => categories.filter((c) => c.type === "expense"), [categories]);
  const incomeCategories = useMemo(() => categories.filter((c) => c.type === "income"), [categories]);

  // UI state
  const {
    loading,
    setLoading,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    selectedPeriod,
    setSelectedPeriod,
    currentPeriodOffset,
    setCurrentPeriodOffset,
  } = useAnalyticsUIState();

  // State for selected month (clicked blue ball)
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  // Track if user has manually deselected a month (to prevent auto-selecting current month)
  const userDeselectedRef = useRef<boolean>(false);
  
  // Animation state for chart - hide initially, show with animation
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  const [readyToShowChart, setReadyToShowChart] = useState<boolean>(false);
  const hasAnimatedOnMountRef = useRef<boolean>(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousDataHashRef = useRef<string>("");
  
  // State for transaction list (matching Activity page)
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  const [expandedAdjustmentId, setExpandedAdjustmentId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionWithDate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewName, setPreviewName] = useState<string>("");
  const [previewType, setPreviewType] = useState<string | null>(null);
  const [previewExpiresAt, setPreviewExpiresAt] = useState<number | null>(null);
  const [attachmentErrorSnackbar, setAttachmentErrorSnackbar] = useState<string | null>(null);
  
  // Centered "Adjustment not editable" message (show then fade out after ~1s)
  const [showAdjustmentMessage, setShowAdjustmentMessage] = useState(false);
  const [adjustmentMessageVisible, setAdjustmentMessageVisible] = useState(false);
  const showAdjustmentNotEditable = useCallback(() => {
    setShowAdjustmentMessage(true);
    setAdjustmentMessageVisible(true);
  }, []);
  useEffect(() => {
    if (!showAdjustmentMessage || !adjustmentMessageVisible) return;
    const t = setTimeout(() => setAdjustmentMessageVisible(false), 1000);
    return () => clearTimeout(t);
  }, [showAdjustmentMessage, adjustmentMessageVisible]);
  useEffect(() => {
    if (!showAdjustmentMessage || adjustmentMessageVisible) return;
    const t = setTimeout(() => setShowAdjustmentMessage(false), 350);
    return () => clearTimeout(t);
  }, [showAdjustmentMessage, adjustmentMessageVisible]);
  
  // State for history dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const { transactionHistory, loadingHistory, fetchTransactionHistory } = useTransactionHistory();

  // Desktop: edit transaction in a popup on this page (no navigation to Activity)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState<TransactionWithDate | null>(null);
  
  // Note: theme and isMobile are declared later in the component

  // Helper functions from Analytics
  const {
    formatAmount,
    convertToDefaultCurrency,
    getAccountName,
    setDateRangeByPeriod: setDateRangeByPeriodHelper,
    getCurrencySymbol,
    getUserName: getUserNameAnalytics,
  } = useAnalyticsHelpers({
    currencies,
    accounts,
    workspaceMembers,
    defaultCurrency,
    workspaceCreatedAt,
    transactions,
  });

  // Helper functions from Activity (for transaction list)
  const {
    formatDate,
    formatTime,
    getCategoryIcon,
    getAttachmentIcon,
    getUserName,
    getUserPhotoURL,
  } = useActivityHelpers({
    workspaceMembers,
    transactions,
    timeFormat,
    dateFormat,
  });

  // Currency helpers
  const { calculateTransferRate } = useCurrencyHelpers(currencies, defaultCurrency);

  // Simple helper functions for transaction list
  const isProtectedAdjustment = (transaction: TransactionWithDate | null) => {
    if (!transaction || transaction.type !== "adjustment") return false;
    return transaction.isInitialBalance === true;
  };
  
  const usesArchivedAccount = (transaction: TransactionWithDate) => {
    if (transaction.type === "transfer") {
      const fromAccount = accounts.find(a => a.id === transaction.fromAccountId);
      const toAccount = accounts.find(a => a.id === transaction.toAccountId);
      return (fromAccount?.archivedAt != null) || (toAccount?.archivedAt != null);
    } else if (transaction.accountId) {
      const account = accounts.find(a => a.id === transaction.accountId);
      return account?.archivedAt != null;
    }
    return false;
  };
  
  const getArchivedAccountNames = (transaction: TransactionWithDate) => {
    const names: string[] = [];
    if (transaction.type === "transfer") {
      const fromAccount = accounts.find(a => a.id === transaction.fromAccountId);
      const toAccount = accounts.find(a => a.id === transaction.toAccountId);
      if (fromAccount?.archivedAt != null) names.push(fromAccount.accountName);
      if (toAccount?.archivedAt != null) names.push(toAccount.accountName);
    } else if (transaction.accountId) {
      const account = accounts.find(a => a.id === transaction.accountId);
      if (account?.archivedAt != null) names.push(account.accountName);
    }
    return names;
  };

  // Helper functions for transaction list
  const getAccountById = (accountId: string | undefined) => {
    return accounts.find(acc => acc.id === accountId);
  };

  const getAccountGroup = (accountId: string | undefined) => {
    const account = getAccountById(accountId);
    return account?.group || "";
  };

  // Same order as transaction create popup: category enabledDetails, in group details order
  const getDetailOrder = useCallback((transaction: TransactionWithDate): string[] => {
    const categoryName = transaction.category;
    const details = transaction.details || {};
    if (!categoryName) return Object.keys(details);
    const category = categories.find((c) => c.name === categoryName);
    if (!category?.groupId) return Object.keys(details);
    const group = groups.find((g) => g.id === category.groupId);
    const enabledDetailNames = Array.isArray(category.enabledDetails) ? category.enabledDetails : [];
    const ordered = (group?.details || []).filter((d) => enabledDetailNames.includes(d.name)).map((d) => d.name);
    if (ordered.length === 0) return Object.keys(details);
    const orderedSet = new Set(ordered);
    const rest = Object.keys(details).filter((k) => !orderedSet.has(k));
    return [...ordered.filter((k) => k in details), ...rest];
  }, [categories, groups]);

  /** All attachments for a transaction: legacy attachmentUrl + attachment-type custom fields. Dedupes by URL. */
  const getTransactionAttachments = useCallback(
    (transaction: TransactionWithDate): { url: string; name: string; type: string; label?: string }[] => {
      const list: { url: string; name: string; type: string; label?: string }[] = [];
      const seenUrls = new Set<string>();
      const txType = transaction.type;
      const addIfNew = (url: string, name: string, type: string, label?: string) => {
        if (!url || seenUrls.has(url)) return;
        seenUrls.add(url);
        list.push({ url, name: name || "Attachment", type: type || "", label });
      };
      if (transaction.attachmentUrl) {
        addIfNew(
          transaction.attachmentUrl,
          transaction.attachmentName || "Attachment",
          transaction.attachmentType || ""
        );
      }
      transactionCustomFields
        .filter((f) => {
          if (f.type !== "attachment") return false;
          if (txType === "adjustment") return false;
          return fieldAppliesToScope(f, txType as TransactionCustomFieldScopeType);
        })
        .forEach((field) => {
          const val = transaction.customFieldValues?.[field.id];
          if (val && typeof val === "object" && "url" in val && (val as CustomFieldAttachmentValue).url) {
            const att = val as CustomFieldAttachmentValue;
            addIfNew(att.url, att.name || "Attachment", att.type || "", field.label);
          }
        });
      return list;
    },
    [transactionCustomFields]
  );

  const getDetailUnit = useCallback((transaction: TransactionWithDate, detailName: string): string | undefined => {
    const categoryName = transaction.category;
    if (!categoryName) return undefined;
    const category = categories.find((c) => c.name === categoryName);
    if (!category?.groupId) return undefined;
    const group = groups.find((g) => g.id === category.groupId);
    const detail = group?.details?.find((d) => d.name === detailName);
    return detail?.unit;
  }, [categories, groups]);

  const transactionHasHistory = new Set<string>(); // Empty for now, can be populated if needed
  
  // Handlers for transaction menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: TransactionWithDate) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTransaction(null);
  };

  const handleEdit = () => {
    if (!selectedTransaction) {
      handleMenuClose();
      return;
    }
    const workspaceSlug = window.location.pathname.split('/')[1];
    const currentPath = window.location.pathname + window.location.search;
    // Desktop: open edit dialog on this page (no navigation). Mobile: full-page edit.
    if (!isMobile && workspaceSlug) {
      setTransactionToEdit(selectedTransaction);
      setEditDialogOpen(true);
      handleMenuClose();
      return;
    }
    const transactionType = selectedTransaction.type === "transfer"
      ? "transfer"
      : selectedTransaction.isIncome
        ? "income"
        : "expense";
    navigate(`/${workspaceSlug}/edit-transaction/${transactionType}/${selectedTransaction.id}`, {
      state: { returnUrl: currentPath },
    });
    handleMenuClose();
  };

  const handleDuplicate = () => {
    if (selectedTransaction) {
      // Navigate to create-transaction page with duplicate transaction ID in state
      const workspaceSlug = window.location.pathname.split('/')[1];
      const currentPath = window.location.pathname + window.location.search;
      const transactionType = selectedTransaction.type === "transfer"
        ? "transfer"
        : selectedTransaction.isIncome
          ? "income"
          : "expense";
      // Navigate to create page with duplicateTransactionId in state and returnUrl
      navigate(`/${workspaceSlug}/create-transaction/${transactionType}`, {
        state: { 
          duplicateTransactionId: selectedTransaction.id,
          returnUrl: currentPath 
        }
      });
    }
    handleMenuClose();
  };

  const handleCopyUrl = () => {
    if (selectedTransaction) {
      const workspaceSlug = window.location.pathname.split('/')[1];
      const url = `${window.location.origin}/${workspaceSlug}/transaction/${selectedTransaction.id}`;
      void copyToClipboard(url);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    // Navigate to Activity page for deletion (simpler than implementing full delete logic here)
    if (selectedTransaction) {
      const workspaceSlug = window.location.pathname.split('/')[1];
      navigate(`/${workspaceSlug}/activity?deleteTransactionId=${selectedTransaction.id}`);
    }
    handleMenuClose();
  };

  const handleHistory = async () => {
    // Open history dialog instead of navigating
    if (selectedTransaction) {
      await fetchTransactionHistory(selectedTransaction.id, workspaceId);
      setHistoryDialogOpen(true);
    }
    handleMenuClose();
  };
  
  // Helper function for history dialog (get user name for history entries)
  const getUserNameForHistory = (userId: string): string => {
    return getUserName(userId);
  };

  const handleWheel = () => {}; // No-op for CategoryGroupDetail

  // Format number without currency symbol for chart labels
  const formatNumber = (amount: number): string => {
    const decimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
    const rounded = Math.round(amount * 10000) / 10000;
    
    if (decimalPlaces === 0) {
      return Math.round(rounded).toLocaleString("en-US");
    }
    
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  };

  // formatDate and formatTime are provided by useActivityHelpers above

  // Wrapper for setDateRangeByPeriod
  const setDateRangeByPeriod = (periodType: string, offset: number = 0): void => {
    setDateRangeByPeriodHelper(periodType, offset, setFromDate, setToDate);
  };

  // Custom period change handler - Interval selection (Daily, Weekly, Monthly, Yearly)
  const handlePeriodChangeCustom = (period: string): void => {
    setSelectedPeriod(period);
    setCurrentPeriodOffset(0);
    // Reset navigation direction when changing period
    lastNavigationDirectionRef.current = null;
    
    if (period === "Custom") {
      return;
    }
    if (period === "All time") {
      setDateRangeByPeriod(period, 0);
      return;
    }
    
    // Daily always focuses on current day (ignore existing range)
    if (period === "Daily") {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setFromDate(todayStr);
      setToDate(todayStr);
      return;
    }
    
    // If we have an existing date range, keep it (user wants to see same period in different detail)
    // Only calculate new range if we don't have dates set
    if (fromDate && toDate) {
      // Keep the current date range, just change the interval/grouping
      return;
    }
    
    // For interval-based periods, show a reasonable date range centered on current period
    if (period === "Weekly") {
      // Show last 12 weeks ending with current week
      const today = new Date();
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(today.getDate() - today.getDay()); // Start of current week (Sunday)
      
      const from = new Date(currentWeekStart);
      from.setDate(currentWeekStart.getDate() - (11 * 7)); // 12 weeks ago (including current week)
      
      const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setFromDate(fromStr);
      setToDate(toStr);
      return;
    }
    if (period === "Monthly") {
      // Show last 6 months including current month
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      setFromDate(fromStr);
      setToDate(toStr);
      return;
    }
    if (period === "Yearly") {
      // Show last 5 years including current year
      const today = new Date();
      const from = new Date(today.getFullYear() - 4, 0, 1);
      const to = new Date(today.getFullYear(), 11, 31);
      const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      setFromDate(fromStr);
      setToDate(toStr);
      return;
    }
    setDateRangeByPeriod(period, 0);
  };

  // Navigate to current date range handler
  const handleNavigateToCurrent = (): void => {
    if (!selectedPeriod || selectedPeriod === "All time" || selectedPeriod === "Custom") return;
    
    const today = new Date();
    let from: Date;
    let to: Date;
    
    if (selectedPeriod === "Daily") {
      from = new Date(today);
      to = new Date(today);
    } else if (selectedPeriod === "Weekly") {
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(today.getDate() - today.getDay()); // Start of current week (Sunday)
      from = new Date(currentWeekStart);
      from.setDate(currentWeekStart.getDate() - (11 * 7)); // 12 weeks ago
      to = new Date(today);
    } else if (selectedPeriod === "Monthly") {
      from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (selectedPeriod === "Yearly") {
      from = new Date(today.getFullYear() - 4, 0, 1);
      to = new Date(today.getFullYear(), 11, 31);
    } else {
      return;
    }
    
    const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
    const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
    setFromDate(fromStr);
    setToDate(toStr);
    setCurrentPeriodOffset(0);
    lastNavigationDirectionRef.current = null;
    
    // The useEffect hook will handle scrolling to center the blue line
    // No need for manual scrolling here as the useEffect will trigger when dates change
  };

  // Custom period navigation handler - Navigate based on selected interval
  const handlePeriodNavigationCustom = (direction: "left" | "right"): void => {
    // Prevent rapid clicks
    const now = Date.now();
    if (now - navigationCooldownRef.current < NAVIGATION_COOLDOWN_MS) {
      return;
    }
    navigationCooldownRef.current = now;
    
    // Track navigation direction
    lastNavigationDirectionRef.current = direction;
    
    if (!selectedPeriod || selectedPeriod === "All time" || selectedPeriod === "Custom" || !fromDate || !toDate) return;
    
    const currentFrom = new Date(fromDate + "T00:00:00");
    const currentTo = new Date(toDate + "T23:59:59.999");
    
    let newFrom: Date;
    let newTo: Date;
    
    if (selectedPeriod === "Daily") {
      // Navigate by 30 days (move the 30-day window by 30 days)
      const moveAmount = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
      newFrom = new Date(currentFrom.getTime() + (direction === "left" ? -moveAmount : moveAmount));
      newTo = new Date(currentTo.getTime() + (direction === "left" ? -moveAmount : moveAmount));
    } else if (selectedPeriod === "Weekly") {
      // Navigate by 12 weeks (move the 12-week window by 12 weeks)
      const moveAmount = 12 * 7 * 24 * 60 * 60 * 1000; // 12 weeks in milliseconds
      newFrom = new Date(currentFrom.getTime() + (direction === "left" ? -moveAmount : moveAmount));
      newTo = new Date(currentTo.getTime() + (direction === "left" ? -moveAmount : moveAmount));
    } else if (selectedPeriod === "Monthly") {
      // Navigate by 1 month (move the 6-month window by 1 month)
      newFrom = new Date(currentFrom);
      newFrom.setMonth(currentFrom.getMonth() + (direction === "left" ? -1 : 1));
      newTo = new Date(currentTo);
      newTo.setMonth(currentTo.getMonth() + (direction === "left" ? -1 : 1));
      // Adjust to month boundaries
      newFrom.setDate(1);
      newTo = new Date(newTo.getFullYear(), newTo.getMonth() + 1, 0);
    } else if (selectedPeriod === "Yearly") {
      // Navigate by 5 years (the range duration)
      newFrom = new Date(currentFrom);
      newFrom.setFullYear(currentFrom.getFullYear() + (direction === "left" ? -5 : 5));
      newTo = new Date(currentTo);
      newTo.setFullYear(currentTo.getFullYear() + (direction === "left" ? -5 : 5));
      // Adjust to year boundaries
      newFrom.setMonth(0);
      newFrom.setDate(1);
      newTo.setMonth(11);
      newTo.setDate(31);
    } else if (selectedPeriod?.match(/^\d+\s*months?$/i)) {
      // N-month interval: move window by N months
      const n = parseInt(selectedPeriod.replace(/\D/g, ""), 10) || 12;
      newFrom = new Date(currentFrom);
      newFrom.setMonth(currentFrom.getMonth() + (direction === "left" ? -n : n));
      newTo = new Date(currentTo);
      newTo.setMonth(currentTo.getMonth() + (direction === "left" ? -n : n));
    } else {
      return;
    }
    
    const fromStr = `${newFrom.getFullYear()}-${String(newFrom.getMonth() + 1).padStart(2, '0')}-${String(newFrom.getDate()).padStart(2, '0')}`;
    const toStr = `${newTo.getFullYear()}-${String(newTo.getMonth() + 1).padStart(2, '0')}-${String(newTo.getDate()).padStart(2, '0')}`;
    setFromDate(fromStr);
    setToDate(toStr);
  };

  // Update loading state
  useEffect(() => {
    setLoading(transactionsLoading);
  }, [transactionsLoading, setLoading]);

  // Set default selected month to current month when entering the page or when period changes to Monthly
  useEffect(() => {
    if (selectedPeriod === "Monthly") {
      // If no month is selected, or if we just switched to Monthly period, set to current month
      if (!selectedMonth) {
        const today = new Date();
        const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        setSelectedMonth(currentMonthKey);
      }
    } else {
      // If period is not Monthly, clear selected month
      if (selectedMonth) {
        setSelectedMonth(null);
      }
    }
  }, [selectedPeriod]); // Run when period changes

  // Set period and date range from URL or default to Monthly on mount
  useEffect(() => {
    // If dates are provided in URL, use them directly (preserves the exact range from Analytics)
    if ((urlFromDate || urlToDate) && !initializedFromUrlRef.current) {
      initializedFromUrlRef.current = true;
      if (urlPeriod) {
        setSelectedPeriod(urlPeriod);
      }
      setCurrentPeriodOffset(0);
      lastNavigationDirectionRef.current = null;
      
      if (urlFromDate) {
        setFromDate(urlFromDate);
      }
      if (urlToDate) {
        setToDate(urlToDate);
      }
      return;
    }
    
    // If period is provided in URL but no dates, calculate date range based on period
    if (urlPeriod && !initializedFromUrlRef.current) {
      initializedFromUrlRef.current = true;
      setSelectedPeriod(urlPeriod);
      setCurrentPeriodOffset(0);
      lastNavigationDirectionRef.current = null;
      
      // Set date range based on period - always use current day
      const today = new Date();
      
      // Convert "All time" to "Monthly" if it comes from URL
      if (urlPeriod === "All time") {
        setSelectedPeriod("Monthly");
        const from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
        const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
        setFromDate(fromStr);
        setToDate(toStr);
      } else if (urlPeriod === "Daily") {
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setFromDate(todayStr);
        setToDate(todayStr);
      } else if (urlPeriod === "Weekly") {
        // Show last 12 weeks ending with today (current day)
        const from = new Date(today);
        from.setDate(today.getDate() - (11 * 7)); // 12 weeks ago from today
        const dayOfWeek = from.getDay();
        from.setDate(from.getDate() - dayOfWeek); // Start of that week (Sunday)
        const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
        const toStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setFromDate(fromStr);
        setToDate(toStr);
      } else if (urlPeriod === "Monthly") {
        const from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
        const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
        setFromDate(fromStr);
        setToDate(toStr);
      } else if (urlPeriod === "Yearly") {
        const from = new Date(today.getFullYear() - 4, 0, 1);
        const to = new Date(today.getFullYear(), 11, 31);
        const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
        const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
        setFromDate(fromStr);
        setToDate(toStr);
      }
      return;
    }
    
    // Otherwise, set default if dates are not already set and not initialized from URL
    if (!urlPeriod && !urlFromDate && !urlToDate && !initializedFromUrlRef.current && (!fromDate || !toDate)) {
      initializedFromUrlRef.current = true;
      setSelectedPeriod("Monthly");
      const today = new Date();
      // Show last 6 months
      const from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      
      setFromDate(fromStr);
      setToDate(toStr);
    }
  }, [workspaceId, urlPeriod, urlFromDate, urlToDate, setSelectedPeriod, setFromDate, setToDate]); // Include date params in dependencies
  
  // Safeguard: If "All time" is somehow selected, convert it to Monthly
  useEffect(() => {
    if (selectedPeriod === "All time") {
      setSelectedPeriod("Monthly");
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      setFromDate(fromStr);
      setToDate(toStr);
    }
  }, [selectedPeriod, setSelectedPeriod, setFromDate, setToDate]);

  // Create mapping from category name to group name
  const categoryToGroupMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((cat) => {
      if (cat.groupId) {
        const group = groups.find((g) => g.id === cat.groupId);
        if (group) {
          map.set(cat.name, group.name);
        }
      }
    });
    return map;
  }, [categories, groups]);

  // Filter transactions based on type and name
  const filteredTransactions = useMemo((): TransactionWithDate[] => {
    if (!decodedName || !type) return [];

    return transactions.filter((transaction) => {
      // Filter by date range (parse as local date so transactions on boundary days are included)
      if (fromDate || toDate) {
        const transactionDate = transaction.date;
        const fromDateObj = fromDate ? new Date(fromDate + "T00:00:00") : null;
        const toDateObj = toDate ? new Date(toDate + "T23:59:59.999") : null;

        if (fromDate && transactionDate < fromDateObj!) {
          return false;
        }
        if (toDate && transactionDate > toDateObj!) {
          return false;
        }
      }

      if (type === "account") {
        // For accounts (adjustments), filter by account name
        const accountName = getAccountName(transaction.accountId, transaction.accountName);
        return accountName === decodedName && transaction.type === "adjustment";
      } else if (type === "group") {
        // For groups, filter by group name
        if (transaction.type === "transfer" || transaction.type === "adjustment") return false;
        const transactionCategory = transaction.category || "Other";
        const groupName = categoryToGroupMap.get(transactionCategory);
        return groupName === decodedName;
      } else {
        // For categories, filter by category name
        if (transaction.type === "transfer" || transaction.type === "adjustment") return false;
        return transaction.category === decodedName;
      }
    });
  }, [transactions, decodedName, type, fromDate, toDate, categoryToGroupMap, getAccountName]);

  // Filter transactions by selected month and category/group/account (like Analytics)
  const selectedMonthTransactions = useMemo((): TransactionWithDate[] => {
    if (!selectedMonth || selectedPeriod !== "Monthly" || !decodedName || !type) {
      return [];
    }
    
    // Parse selectedMonth (format: "YYYY-MM")
    const [year, month] = selectedMonth.split("-").map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59);
    
    // Filter directly from all transactions, applying both category/group/account filter AND month filter
    return transactions.filter((transaction) => {
      // Filter by month
      const transactionDate = transaction.date;
      if (transactionDate < monthStart || transactionDate > monthEnd) {
        return false;
      }

      // Filter by category/group/account (same logic as filteredTransactions)
      if (type === "account") {
        // For accounts (adjustments), filter by account name
        const accountName = getAccountName(transaction.accountId, transaction.accountName);
        return accountName === decodedName && transaction.type === "adjustment";
      } else if (type === "group") {
        // For groups, filter by group name
        if (transaction.type === "transfer" || transaction.type === "adjustment") return false;
        const transactionCategory = transaction.category || "Other";
        const groupName = categoryToGroupMap.get(transactionCategory);
        return groupName === decodedName;
      } else {
        // For categories, filter by category name
        if (transaction.type === "transfer" || transaction.type === "adjustment") return false;
        // Normalize category names for comparison (trim whitespace, handle encoding)
        const transactionCategory = (transaction.category || "").trim();
        const normalizedDecodedName = decodedName.trim();
        return transactionCategory === normalizedDecodedName;
      }
    }).sort((a, b) => {
      // Sort by date descending (newest first)
      return b.date.getTime() - a.date.getTime();
    });
  }, [selectedMonth, selectedPeriod, transactions, decodedName, type, categoryToGroupMap, getAccountName]);

  // Determine transaction type for chart color - use category/group type so all income
  // categories show green. Prefer urlTab (from Analytics tab) so Income tab opens with green label/graph.
  const transactionType = useMemo(() => {
    if (type === "account") {
      if (!filteredTransactions.length) return null;
      return "adjustments";
    }
    if (type === "category") {
      // When URL has tab=incomes/expenses, find category of that type (avoids wrong type when same name exists in both)
      const cat = urlTab === "incomes"
        ? categories.find((c) => c.name === decodedName && c.type === "income")
        : urlTab === "expenses"
          ? categories.find((c) => c.name === decodedName && c.type === "expense")
          : categories.find((c) => c.name === decodedName);
      if (urlTab === "incomes" || urlTab === "expenses") return urlTab;
      if (cat?.type === "income") return "incomes";
      return "expenses";
    }
    if (type === "group") {
      const grp = urlTab === "incomes"
        ? groups.find((g) => g.name === decodedName && g.type === "income")
        : urlTab === "expenses"
          ? groups.find((g) => g.name === decodedName && g.type === "expense")
          : groups.find((g) => g.name === decodedName);
      if (urlTab === "incomes" || urlTab === "expenses") return urlTab;
      if (grp?.type === "income") return "incomes";
      return "expenses";
    }
    if (!filteredTransactions.length) return null;
    const firstTransaction = filteredTransactions[0];
    if (firstTransaction.type === "adjustment") return "adjustments";
    if (firstTransaction.type === "income") return "incomes";
    return "expenses";
  }, [type, decodedName, categories, groups, filteredTransactions, urlTab]);

  // Group transactions by interval based on selectedPeriod
  const chartData = useMemo(() => {
    if (!fromDate || !toDate) {
      return [];
    }

    // Parse as local date so transactions on boundary days are included in the chart
    const originalFrom = new Date(fromDate + "T00:00:00");
    const originalTo = new Date(toDate + "T23:59:59.999");

    // Extend date range to show 3 intervals before and 3 intervals after
    let chartFrom = new Date(originalFrom);
    let chartTo = new Date(originalTo);
    
    // Determine how much to extend based on selectedPeriod
    if (selectedPeriod === "Daily") {
      chartFrom.setDate(chartFrom.getDate() - 3); // 3 days before
      chartTo.setDate(chartTo.getDate() + 3); // 3 days after
    } else if (selectedPeriod === "Weekly") {
      chartFrom.setDate(chartFrom.getDate() - (3 * 7)); // 3 weeks before
      chartTo.setDate(chartTo.getDate() + (3 * 7)); // 3 weeks after
    } else if (selectedPeriod === "Monthly") {
      chartFrom.setMonth(chartFrom.getMonth() - 3); // 3 months before
      chartTo.setMonth(chartTo.getMonth() + 3); // 3 months after
      // Adjust to end of month
      chartTo = new Date(chartTo.getFullYear(), chartTo.getMonth() + 1, 0);
    } else if (selectedPeriod === "Yearly") {
      chartFrom.setFullYear(chartFrom.getFullYear() - 3); // 3 years before
      chartTo.setFullYear(chartTo.getFullYear() + 3); // 3 years after
      chartTo = new Date(chartTo.getFullYear(), 11, 31); // End of year
    }
    
    // Filter transactions for extended chart range
    const chartTransactions = transactions.filter((transaction) => {
      // Filter by extended date range for chart
      const transactionDate = transaction.date;
      const fromDateObj = chartFrom;
      const toDateObj = new Date(chartTo);
      toDateObj.setHours(23, 59, 59);

      if (transactionDate < fromDateObj) {
        return false;
      }
      if (transactionDate > toDateObj) {
        return false;
      }

      // Filter by type and name (same as filteredTransactions)
      if (type === "account") {
        const accountName = getAccountName(transaction.accountId, transaction.accountName);
        return accountName === decodedName && transaction.type === "adjustment";
      } else if (type === "group") {
        if (transaction.type === "transfer" || transaction.type === "adjustment") return false;
        const transactionCategory = transaction.category || "Other";
        const groupName = categoryToGroupMap.get(transactionCategory);
        return groupName === decodedName;
      } else {
        if (transaction.type === "transfer" || transaction.type === "adjustment") return false;
        return transaction.category === decodedName;
      }
    });

    // Don't return early - we want to show empty intervals too (with 0 values) to connect lines
    const dataMap = new Map<string, number>();
    const from = chartFrom;
    const to = chartTo;

    // Determine interval based on selectedPeriod
    let interval: "day" | "week" | "month" | "year";
    let dateFormat: (date: Date) => string;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    if (selectedPeriod === "Daily") {
      interval = "day";
      dateFormat = (d) => {
        return `${String(d.getDate()).padStart(2, '0')} ${monthNames[d.getMonth()]}`;
      };
    } else if (selectedPeriod === "Weekly") {
      interval = "week";
      dateFormat = (d) => {
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay()); // Start of week (Sunday)
        const day = weekStart.getDate();
        const month = monthNames[weekStart.getMonth()];
        return `${month} ${day}`;
      };
    } else if (selectedPeriod === "Monthly") {
      interval = "month";
      dateFormat = (d) => monthNames[d.getMonth()];
    } else if (selectedPeriod === "Yearly") {
      interval = "year";
      dateFormat = (d) => `${d.getFullYear()}`;
    } else {
      // Custom or All time - determine based on date range
      const daysDiff = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff <= 7) {
        interval = "day";
        dateFormat = (d) => {
          const day = d.getDate();
          const month = monthNames[d.getMonth()];
          return `${month} ${day}`;
        };
      } else if (daysDiff <= 90) {
        interval = "week";
        dateFormat = (d) => {
          const weekStart = new Date(d);
          weekStart.setDate(d.getDate() - d.getDay());
          const day = weekStart.getDate();
          const month = monthNames[weekStart.getMonth()];
          return `${month} ${day}`;
        };
      } else if (daysDiff <= 365) {
        interval = "month";
        dateFormat = (d) => monthNames[d.getMonth()];
      } else {
        interval = "year";
        dateFormat = (d) => `${d.getFullYear()}`;
      }
    }

    // Initialize all intervals in range
    const current = new Date(from);
    // Set to start of interval for proper initialization
    if (interval === "month") {
      current.setDate(1);
    } else if (interval === "year") {
      current.setMonth(0);
      current.setDate(1);
    } else if (interval === "week") {
      current.setDate(current.getDate() - current.getDay());
    } else if (interval === "day" && selectedPeriod === "Daily") {
      current.setHours(0, 0, 0, 0);
    }

    while (current <= to) {
      let key: string;
      if (interval === "day") {
        key = dateFormat(current);
        if (selectedPeriod === "Daily") {
          current.setDate(current.getDate() + 1);
        } else {
          current.setDate(current.getDate() + 1);
        }
      } else if (interval === "week") {
        const weekStart = new Date(current);
        weekStart.setDate(current.getDate() - current.getDay());
        key = dateFormat(weekStart);
        current.setDate(current.getDate() + 7);
      } else if (interval === "month") {
        key = dateFormat(current);
        current.setMonth(current.getMonth() + 1);
      } else if (interval === "year") {
        key = dateFormat(current);
        current.setFullYear(current.getFullYear() + 1);
      } else {
        key = dateFormat(current);
        current.setFullYear(current.getFullYear() + 1);
      }
      if (!dataMap.has(key)) {
        dataMap.set(key, 0);
      }
    }

    // Aggregate transactions by interval
    chartTransactions.forEach((transaction) => {
      const transactionDate = new Date(transaction.date);
      let key: string;

      if (interval === "day") {
        if (selectedPeriod === "Daily") {
          key = dateFormat(transactionDate);
        } else {
          key = dateFormat(transactionDate);
        }
      } else if (interval === "week") {
        const weekStart = new Date(transactionDate);
        weekStart.setDate(transactionDate.getDate() - transactionDate.getDay());
        key = dateFormat(weekStart);
      } else if (interval === "month") {
        // For monthly period, include year in key to prevent grouping transactions from different years
        // Use sortKey format (YYYY-MM) for grouping, but display will still show just month name
        key = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;
      } else if (interval === "year") {
        key = dateFormat(transactionDate);
      } else {
        key = dateFormat(transactionDate);
      }

      const amount = convertToDefaultCurrency(
        transaction.type === "adjustment" ? transaction.amount : Math.abs(transaction.amount || 0),
        transaction.currency
      );
      dataMap.set(key, (dataMap.get(key) || 0) + amount);
    });

    // Convert to array and create sortable entries with proper ordering
    const entries: Array<{ date: string; amount: number; sortKey: string }> = [];
    
    // Re-iterate through the date range to maintain chronological order
    const sortedCurrent = new Date(from);
    if (interval === "month") {
      sortedCurrent.setDate(1);
    } else if (interval === "year") {
      sortedCurrent.setMonth(0);
      sortedCurrent.setDate(1);
    } else if (interval === "week") {
      sortedCurrent.setDate(sortedCurrent.getDate() - sortedCurrent.getDay());
    } else if (interval === "day" && selectedPeriod === "Daily") {
      sortedCurrent.setHours(0, 0, 0, 0);
    }

    while (sortedCurrent <= to) {
      let key: string;
      let sortKey: string;
      
      if (interval === "day") {
        if (selectedPeriod === "Daily") {
          key = dateFormat(sortedCurrent);
          sortKey = `${sortedCurrent.getFullYear()}-${String(sortedCurrent.getMonth() + 1).padStart(2, '0')}-${String(sortedCurrent.getDate()).padStart(2, '0')}`;
          sortedCurrent.setDate(sortedCurrent.getDate() + 1);
        } else {
          key = dateFormat(sortedCurrent);
          sortKey = `${sortedCurrent.getFullYear()}-${String(sortedCurrent.getMonth() + 1).padStart(2, '0')}-${String(sortedCurrent.getDate()).padStart(2, '0')}`;
          sortedCurrent.setDate(sortedCurrent.getDate() + 1);
        }
      } else if (interval === "week") {
        const weekStart = new Date(sortedCurrent);
        weekStart.setDate(sortedCurrent.getDate() - sortedCurrent.getDay());
        key = dateFormat(weekStart);
        sortKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
        sortedCurrent.setDate(sortedCurrent.getDate() + 7);
      } else if (interval === "month") {
        key = dateFormat(sortedCurrent);
        sortKey = `${sortedCurrent.getFullYear()}-${String(sortedCurrent.getMonth() + 1).padStart(2, '0')}`;
        sortedCurrent.setMonth(sortedCurrent.getMonth() + 1);
      } else if (interval === "year") {
        key = dateFormat(sortedCurrent);
        sortKey = `${sortedCurrent.getFullYear()}`;
        sortedCurrent.setFullYear(sortedCurrent.getFullYear() + 1);
      } else {
        key = dateFormat(sortedCurrent);
        sortKey = key;
        sortedCurrent.setFullYear(sortedCurrent.getFullYear() + 1);
      }
      
      // Always push entry (even if amount is 0) to maintain consistent spacing for year labels
      // For monthly period, use sortKey (YYYY-MM) to lookup amounts to prevent cross-year grouping
      const lookupKey = interval === "month" ? sortKey : key;
      entries.push({
        date: key,
        amount: Math.round((dataMap.get(lookupKey) || 0) * 100) / 100,
        sortKey,
      });
    }

    // Sort by sortKey to ensure chronological order and add year info for monthly/yearly periods
    const sortedEntries = entries.sort((a, b) => a.sortKey.localeCompare(b.sortKey)).map(({ date, amount, sortKey }) => {
      // For monthly/yearly periods, extract year from sortKey
      let year: number | undefined;
      if (interval === "month" && sortKey.includes("-")) {
        year = parseInt(sortKey.split("-")[0]);
      } else if (interval === "year") {
        year = parseInt(sortKey);
      }
      
      // Check if this entry is within the selected range
      let isInSelectedRange = false;
      const entryDate = new Date(sortKey.includes("-") ? sortKey : `${sortKey}-01-01`);
      
      if (interval === "day") {
        const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        const fromDateOnly = new Date(originalFrom.getFullYear(), originalFrom.getMonth(), originalFrom.getDate());
        const toDateOnly = new Date(originalTo.getFullYear(), originalTo.getMonth(), originalTo.getDate());
        isInSelectedRange = entryDateOnly >= fromDateOnly && entryDateOnly <= toDateOnly;
      } else if (interval === "week") {
        // For weeks, check if the week overlaps with the selected range
        const weekStart = new Date(entryDate);
        weekStart.setDate(entryDate.getDate() - entryDate.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        isInSelectedRange = weekStart <= originalTo && weekEnd >= originalFrom;
      } else if (interval === "month") {
        const monthStart = new Date(entryDate.getFullYear(), entryDate.getMonth(), 1);
        const monthEnd = new Date(entryDate.getFullYear(), entryDate.getMonth() + 1, 0);
        isInSelectedRange = monthStart <= originalTo && monthEnd >= originalFrom;
      } else if (interval === "year") {
        const yearStart = new Date(entryDate.getFullYear(), 0, 1);
        const yearEnd = new Date(entryDate.getFullYear(), 11, 31);
        isInSelectedRange = yearStart <= originalTo && yearEnd >= originalFrom;
      }
      
      return { date, amount, year, isInSelectedRange, sortKey };
    });
    
    return sortedEntries;
  }, [transactions, fromDate, toDate, selectedPeriod, convertToDefaultCurrency, categoryToGroupMap, getAccountName, decodedName, type]);

  // Track if we should show baseline data for animation
  const [showBaselineData, setShowBaselineData] = useState<boolean>(true);
  
  // Create animated chart data - starts from baseline (0) and animates to final values
  const animatedChartData = useMemo(() => {
    // Hide chart until ready to prevent blink
    if (!readyToShowChart || !shouldAnimate) {
      return [];
    }
    // Return baseline data (zeros) first, then switch to real data to trigger animation
    if (showBaselineData && !hasAnimatedOnMountRef.current) {
      return chartData.map(entry => ({
        ...entry,
        amount: 0, // Start from baseline for animation
      }));
    }
    // Return real data - Recharts will animate from baseline to this
    return chartData;
  }, [chartData, readyToShowChart, shouldAnimate, showBaselineData]);

  // Calculate chart width based on number of data points for horizontal scrolling
  // On desktop, use full container width; on mobile, calculate based on data points
  // Note: isMobile is declared later, so we'll check it in the component render
  const chartWidth = useMemo(() => {
    if (chartData.length === 0) return "100%";
    // Mobile: Minimum 80px per data point for readability, but at least 600px total
    // Desktop will use 100% width (handled in render)
    const calculatedWidth = Math.max(chartData.length * 80, 600);
    return calculatedWidth;
  }, [chartData.length]);

  // Reset animation state when category/group/account changes
  useEffect(() => {
    // Reset animation state when navigating to a different category/group/account
    hasAnimatedOnMountRef.current = false;
    previousDataHashRef.current = "";
    setShouldAnimate(false);
    setReadyToShowChart(false);
    setShowBaselineData(true);
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
  }, [type, decodedName]);

  // Animation trigger - handles initial mount and data changes
  useEffect(() => {
    // Skip if still loading
    if (loading || transactionsLoading) {
      return;
    }

    // Check if this is initial mount with data ready
    const isInitialMount = !hasAnimatedOnMountRef.current && chartData.length > 0;

    // Check if data changed (but not initial mount)
    const chartDataHash = chartData.map(d => `${d.sortKey}:${d.amount}`).join('|');
    const isDataChange = chartDataHash !== previousDataHashRef.current && hasAnimatedOnMountRef.current;

    // Trigger animation if initial mount or data change
    if (isInitialMount || isDataChange) {
      // Clear any existing timer
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }

      // Update refs immediately to prevent re-triggering
      if (isDataChange || isInitialMount) {
        previousDataHashRef.current = chartDataHash;
      }

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Set both flags in the same frame to prevent blink
          // First set readyToShowChart, then immediately set shouldAnimate
          setReadyToShowChart(true);

          // Use requestAnimationFrame to ensure both state updates happen before render
          requestAnimationFrame(() => {
            setShouldAnimate(true);

            // Switch from baseline to real data after a brief moment to trigger animation
            setTimeout(() => {
              setShowBaselineData(false);
            }, 100);

            // After animation completes, mark as animated
            if (isInitialMount) {
              setTimeout(() => {
                hasAnimatedOnMountRef.current = true;
              }, 1300); // After animation completes (1200ms + small buffer)
            }
          });
        });
      });
    }

    // Cleanup function
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, [loading, transactionsLoading, chartData]);

  // Render dot component - memoized to prevent unnecessary re-renders
  // Keep selectedMonth in deps so dots update visually, but optimize to prevent chart hiding
  const renderDot = useCallback((props: any) => {
    const { cx, cy, payload } = props;
    
    // Return null if coordinates are invalid (Recharts handles this correctly)
    if (cx == null || cy == null) {
      return null;
    }
    
    const isInSelectedRange = payload?.isInSelectedRange;
    const isClicked = selectedPeriod === "Monthly" && payload?.sortKey === selectedMonth;
    // When a month is selected, only highlight that month (ignore date range highlighting)
    const isSelected = selectedMonth ? isClicked : isInSelectedRange;
    const radius = isSelected ? 6 : 4;
    const canClick = selectedPeriod === "Monthly" && payload?.sortKey;
    
    const handleClick = (e: React.MouseEvent<SVGCircleElement> | React.TouchEvent<SVGCircleElement>) => {
      e.stopPropagation();
      // Note: preventDefault removed to avoid passive event listener warnings
      // stopPropagation is sufficient for preventing event bubbling
      if (payload?.sortKey) {
        // Update immediately - ensures the chart updates without delay
        setSelectedMonth(payload.sortKey);
      }
    };
    
    return (
      <g>
        {/* Invisible larger circle for easier clicking - always render for Monthly period */}
        {canClick && (
          <circle 
            cx={cx} 
            cy={cy} 
            r={20}
            fill="transparent"
            stroke="transparent"
            strokeWidth={0}
            style={{ 
              cursor: "pointer", 
              pointerEvents: "all",
            }}
            onClick={(e) => handleClick(e)}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              // Note: preventDefault removed to avoid passive event listener warnings
              handleClick(e);
            }}
          />
        )}
        {/* Visible circle */}
        <circle 
          cx={cx} 
          cy={cy} 
          r={radius} 
          fill={isSelected ? "#3B82F6" : (transactionType === "incomes" ? "#22C55E" : "#EF4444")} 
          stroke={isSelected ? "#1E40AF" : "none"}
          strokeWidth={isSelected ? 2 : 0}
          style={{ 
            cursor: canClick ? "pointer" : "default", 
            pointerEvents: canClick ? "all" : "none",
          }}
          onClick={(e) => handleClick(e)}
          onMouseDown={canClick ? (e) => {
            e.stopPropagation();
            handleClick(e);
          } : undefined}
          onTouchStart={canClick ? (e) => {
            e.stopPropagation();
            // Note: preventDefault removed to avoid passive event listener warnings
            handleClick(e);
          } : undefined}
        />
      </g>
    );
  }, [selectedMonth, selectedPeriod, transactionType]); // Keep selectedMonth so dots update visually

  // Navigation handlers for arrow buttons - allow unlimited navigation
  const handlePreviousPoint = useCallback(() => {
    if (selectedPeriod !== "Monthly" || !selectedMonth) return;
    
    // Parse current selected month
    const [year, month] = selectedMonth.split("-").map(Number);
    const currentMonthDate = new Date(year, month - 1, 1);
    
    // Calculate previous month
    const previousMonthDate = new Date(currentMonthDate);
    previousMonthDate.setMonth(previousMonthDate.getMonth() - 1);
    const previousMonthKey = `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Check if we need to expand the date range
    if (fromDate) {
      const fromDateObj = new Date(fromDate + "T00:00:00");
      const previousMonthStart = new Date(previousMonthDate.getFullYear(), previousMonthDate.getMonth(), 1);
      
      // If previous month is before current fromDate, expand the range
      if (previousMonthStart < fromDateObj) {
        // Expand date range to include previous month (and a few more for context)
        const newFrom = new Date(previousMonthDate);
        newFrom.setMonth(newFrom.getMonth() - 2); // Include 2 months before for context
        newFrom.setDate(1);
        const newFromStr = `${newFrom.getFullYear()}-${String(newFrom.getMonth() + 1).padStart(2, '0')}-${String(newFrom.getDate()).padStart(2, '0')}`;
        setFromDate(newFromStr);
      }
    }
    
    setSelectedMonth(previousMonthKey);
  }, [selectedMonth, selectedPeriod, fromDate, setFromDate]);

  const handleNextPoint = useCallback(() => {
    if (selectedPeriod !== "Monthly" || !selectedMonth) return;
    
    // Parse current selected month
    const [year, month] = selectedMonth.split("-").map(Number);
    const currentMonthDate = new Date(year, month - 1, 1);
    
    // Calculate next month
    const nextMonthDate = new Date(currentMonthDate);
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
    const nextMonthKey = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Check if we need to expand the date range
    if (toDate) {
      const toDateObj = new Date(toDate + "T23:59:59.999");
      const nextMonthEnd = new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth() + 1, 0);
      
      // If next month is after current toDate, expand the range
      if (nextMonthEnd > toDateObj) {
        // Expand date range to include next month (and a few more for context)
        const newTo = new Date(nextMonthDate);
        newTo.setMonth(newTo.getMonth() + 2); // Include 2 months after for context
        newTo.setDate(0); // Last day of that month
        const newToStr = `${newTo.getFullYear()}-${String(newTo.getMonth() + 1).padStart(2, '0')}-${String(newTo.getDate()).padStart(2, '0')}`;
        setToDate(newToStr);
      }
    }
    
    setSelectedMonth(nextMonthKey);
  }, [selectedMonth, selectedPeriod, toDate, setToDate]);

  // Check if navigation buttons should be enabled - allow unlimited navigation
  // Users can navigate to any month, date range will expand automatically
  const canNavigatePrevious = useMemo(() => {
    if (selectedPeriod !== "Monthly" || !selectedMonth) return false;
    // Always allow previous navigation - unlimited
    return true;
  }, [selectedMonth, selectedPeriod]);

  const canNavigateNext = useMemo(() => {
    if (selectedPeriod !== "Monthly" || !selectedMonth) return false;
    // Always allow next navigation - unlimited
    return true;
  }, [selectedMonth, selectedPeriod]);

  // Track previous chartData length to detect actual data changes (not just re-renders)
  const prevChartDataLengthRef = useRef<number>(0);
  const prevChartDataLengthForScrollRef = useRef<number>(0);
  const prevFromDateRef = useRef<string | null>(null);
  const prevToDateRef = useRef<string | null>(null);
  const prevSelectedPeriodRef = useRef<string | null>(null);

  // Scroll to selected month. Use instant scroll only when expanding (prepending) to avoid
  // "move right, stop, come back"; use smooth scroll for in-range switches (like forward button).
  const performScrollToSelected = useCallback(() => {
    const el = chartScrollRef.current;
    if (!el || !chartData.length || selectedPeriod !== "Monthly" || !selectedMonth) return;
    const monthIndex = chartData.findIndex(entry => entry.sortKey === selectedMonth);
    if (monthIndex < 0) return;
    const pointWidth = 80;
    const containerWidth = el.clientWidth;
    const targetPosition = monthIndex * pointWidth + (pointWidth / 2);
    const centerPosition = targetPosition - (containerWidth / 2);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const isExpanding = chartData.length > prevChartDataLengthForScrollRef.current;
    prevChartDataLengthForScrollRef.current = chartData.length;
    el.style.scrollBehavior = isExpanding ? "auto" : "smooth";
    el.scrollLeft = Math.max(0, Math.min(centerPosition, maxScroll));
  }, [chartData, selectedMonth, selectedPeriod]);

  // Run scroll synchronously before paint when selectedMonth or date range changes
  useLayoutEffect(() => {
    if (selectedPeriod !== "Monthly" || !selectedMonth || !chartData.length) return;
    performScrollToSelected();
  }, [selectedMonth, chartData, selectedPeriod, performScrollToSelected]);

  // Auto-scroll chart to show selected range when dates change (delayed pass for layout stabilization)
  useEffect(() => {
    // Only scroll if dates or period actually changed, not on every render
    const datesChanged = prevFromDateRef.current !== fromDate || prevToDateRef.current !== toDate;
    const periodChanged = prevSelectedPeriodRef.current !== selectedPeriod;
    const dataLengthChanged = prevChartDataLengthRef.current !== chartData.length;
    
    // Update refs
    prevFromDateRef.current = fromDate;
    prevToDateRef.current = toDate;
    prevSelectedPeriodRef.current = selectedPeriod;
    prevChartDataLengthRef.current = chartData.length;
    
    // Only proceed if dates, period, or data length actually changed
    if (!datesChanged && !periodChanged && !dataLengthChanged) {
      return;
    }
    
    if (!chartScrollRef.current || !chartData.length || !fromDate || !toDate) return;
    
    // Find the index of the selected range start
    const selectedStartIndex = chartData.findIndex(entry => entry.isInSelectedRange);
    if (selectedStartIndex < 0) return;
    
    // Calculate scroll position to center the selected range
    // Each data point is approximately 80px wide
    const pointWidth = 80;
    const containerWidth = chartScrollRef.current.clientWidth;
    const selectedEndIndex = chartData.length - 1 - [...chartData].reverse().findIndex(entry => entry.isInSelectedRange);
    
    if (selectedEndIndex < 0) return;
    
    // For Monthly period with selected month, center on that (avoids conflicting scroll with selectedMonth effect).
    // Otherwise try current month (today), else center on the middle of the selected range.
    let targetIndex = Math.floor((selectedStartIndex + selectedEndIndex) / 2);
    
    if (selectedPeriod === "Monthly") {
      if (selectedMonth) {
        const monthIdx = chartData.findIndex(entry => entry.sortKey === selectedMonth);
        if (monthIdx >= 0) targetIndex = monthIdx;
      } else {
        const today = new Date();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const currentMonthName = monthNames[today.getMonth()];
        for (let i = selectedStartIndex; i <= selectedEndIndex; i++) {
          const entry = chartData[i];
          if (entry && entry.date === currentMonthName) {
            targetIndex = i;
            break;
          }
        }
      }
    }
    
    // Calculate scroll position to center the target index in the viewport
    const selectedRangeCenter = targetIndex * pointWidth + (pointWidth / 2);
    const centerPosition = selectedRangeCenter - (containerWidth / 2);
    
    // Skip delayed scroll when selectedMonth - useLayoutEffect already handles arrow navigation.
    // Delayed scroll only for initial load (no selected month yet).
    if (selectedMonth) return;

    // Scroll to center the selected range - use instant scroll to avoid animation conflict
    setTimeout(() => {
      requestAnimationFrame(() => {
        const el = chartScrollRef.current;
        if (el) {
          const maxScroll = el.scrollWidth - el.clientWidth;
          const prevBehavior = el.style.scrollBehavior;
          el.style.scrollBehavior = "auto";
          el.scrollLeft = Math.max(0, Math.min(centerPosition, maxScroll));
          el.style.scrollBehavior = prevBehavior;
        }
      });
    }, 150);
  }, [chartData, fromDate, toDate, selectedPeriod, selectedMonth]);

  // Calculate total amount
  // Use selectedMonthTransactions if a month is selected (Monthly period), otherwise use filteredTransactions
  const totalAmount = useMemo(() => {
    // Use selectedMonthTransactions if a month is selected (even if empty - shows 0 for that month)
    // Otherwise use filteredTransactions (filtered by fromDate/toDate)
    const transactionsToUse = (selectedMonth && selectedPeriod === "Monthly")
      ? selectedMonthTransactions
      : filteredTransactions;
    
    return transactionsToUse.reduce((sum, transaction) => {
      const amount = convertToDefaultCurrency(
        transaction.type === "adjustment" ? transaction.amount : Math.abs(transaction.amount || 0),
        transaction.currency
      );
      return sum + amount;
    }, 0);
  }, [filteredTransactions, selectedMonthTransactions, selectedMonth, selectedPeriod, convertToDefaultCurrency]);

  // Calculate sub-category breakdown (for groups, show categories; for categories, show nothing)
  // Use selectedMonthTransactions if a month is selected (Monthly period), otherwise use filteredTransactions
  const subCategoryData = useMemo(() => {
    if (type !== "group") return []; // Only show sub-categories for groups

    // Use selectedMonthTransactions if a month is selected (even if empty - shows 0 for that month)
    // Otherwise use filteredTransactions (filtered by fromDate/toDate)
    const transactionsToUse = (selectedMonth && selectedPeriod === "Monthly")
      ? selectedMonthTransactions
      : filteredTransactions;

    const categoryMap = new Map<string, number>();
    
    transactionsToUse.forEach((transaction) => {
      if (transaction.type === "transfer" || transaction.type === "adjustment") return;
      const categoryName = transaction.category || "Other";
      
      const amount = convertToDefaultCurrency(
        Math.abs(transaction.amount || 0),
        transaction.currency
      );
      categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + amount);
    });

    // Get categories that belong to this group FIRST
    const group = groups.find((g) => g.name === decodedName);
    if (!group) return [];

    const groupCategories = categories.filter((cat) => cat.groupId === group.id);
    // Sort categories by their configured order field (default to 0 if not present)
    groupCategories.sort((a, b) => {
      const orderA = (a as any).order ?? 0;
      const orderB = (b as any).order ?? 0;
      return orderA - orderB;
    });
    
    // Create a map of category name to order for sorting
    const categoryOrderMap = new Map<string, number>();
    groupCategories.forEach((cat, index) => {
      categoryOrderMap.set(cat.name, (cat as any).order ?? index);
    });
    
    // Calculate total amount for percentage calculation
    const totalGroupAmount = Array.from(categoryMap.values()).reduce((sum, val) => sum + Math.abs(val), 0);
    
    // IMPORTANT: Include ALL categories in the group, even if they have 0 transactions
    // This ensures categories with no transactions for the selected month still appear in the list
    return groupCategories
      .map((cat) => {
        const value = Math.round((categoryMap.get(cat.name) || 0) * 100) / 100; // Use 0 if no transactions
        const percentage = totalGroupAmount > 0 ? (Math.abs(value) / totalGroupAmount) * 100 : 0;
        return {
          name: cat.name,
          value: value,
          percentage: percentage,
          order: categoryOrderMap.get(cat.name) ?? 999, // Use high number for categories without order
        };
      })
      .sort((a, b) => {
        // Sort by percentage (highest first) - this is the primary sort
        if (Math.abs(b.percentage - a.percentage) > 0.01) { // Use small threshold to avoid floating point issues
          return b.percentage - a.percentage;
        }
        // If percentages are the same (or very close), sort by value descending as fallback
        if (Math.abs(b.value) !== Math.abs(a.value)) {
          return Math.abs(b.value) - Math.abs(a.value);
        }
        // If values are also the same, use order field as final tiebreaker
        return a.order - b.order;
      })
      .map((item, index) => ({
        name: item.name,
        value: item.value,
        color: getCategoryColor(index),
      }));
  }, [filteredTransactions, selectedMonthTransactions, selectedMonth, selectedPeriod, type, decodedName, groups, categories, convertToDefaultCurrency]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const chartTextColor = theme.palette.text.primary;
  const chartDividerColor = theme.palette.divider;
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState<HTMLElement | null>(null);
  const [focusedDateField, setFocusedDateField] = useState<"startDate" | "endDate" | null>(null);

  // Don't render until defaultCurrency is loaded to prevent showing USD values first
  if (loading || !defaultCurrencyLoaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", bgcolor: "background.default" }}>
      {/* Header with Back Button and Title */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 1.5, 
          mb: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          userSelect: "none", 
          position: "sticky", 
          top: { xs: "0px", sm: "0px" }, 
          zIndex: 8 
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
              <Typography variant="body1" fontWeight="bold" noWrap>
                {decodedName}
              </Typography>
              {/* Show group name as chip (only for categories) */}
              {type === "category" && categoryToGroupMap.get(decodedName) && (
                <Chip
                  label={categoryToGroupMap.get(decodedName)}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": {
                      px: 0.75,
                      py: 0,
                    },
                  }}
                />
              )}
            </Box>
            {/* Show transaction type (Expenses/Income) as chip - on the right */}
            {transactionType && (
              <Chip
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: transactionType === "incomes" ? "#22C55E" : "#EF4444",
                    }}
                  />
                }
                label={transactionType === "expenses" ? "Expenses" : transactionType === "incomes" ? "Income" : "Amount"}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  flexShrink: 0,
                  "& .MuiChip-label": {
                    px: 0.75,
                    py: 0,
                  },
                  "& .MuiChip-icon": {
                    marginLeft: 0.5,
                    marginRight: -0.25,
                  },
                }}
              />
            )}
          </Box>
        </Box>
        {/* Period Selector - Full functionality like MobileAnalytics */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "center" }}>
          {/* Left Arrow - show for all navigable periods (Daily, Weekly, Monthly, Yearly, N-month) */}
          {(selectedPeriod === "Monthly" && selectedMonth && chartData.length > 0) ? (
            <IconButton
              onClick={handlePreviousPoint}
              disabled={!canNavigatePrevious}
              size="small"
              sx={{ "&:disabled": { opacity: 0.3 } }}
            >
              <ChevronLeftIcon />
            </IconButton>
          ) : (selectedPeriod && selectedPeriod !== "All time" && selectedPeriod !== "Custom" && fromDate && toDate && (selectedPeriod === "Daily" || selectedPeriod === "Weekly" || selectedPeriod === "Monthly" || selectedPeriod === "Yearly" || selectedPeriod.match(/^\d+\s*months?$/i))) ? (
            <IconButton onClick={() => handlePeriodNavigationCustom("left")} size="small" sx={{ "&:disabled": { opacity: 0.3 } }}>
              <ChevronLeftIcon />
            </IconButton>
          ) : null}
          
          {/* Period Text Display with Dropdown Arrow */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", minWidth: 0 }}>
            {selectedPeriod === "Custom" ? (
              <DateRangePicker
                startDate={fromDate}
                endDate={toDate}
                onStartDateChange={setFromDate}
                onEndDateChange={setToDate}
                focusedDateField={focusedDateField}
                onFocusedDateFieldChange={setFocusedDateField}
              />
            ) : (
              <Box 
                onClick={(e) => {
                  setPeriodMenuAnchor(e.currentTarget);
                }}
                sx={{ 
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: "action.hover",
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  fontWeight="medium" 
                  sx={{ 
                    fontSize: "0.875rem", 
                    userSelect: "none", 
                    textAlign: "center",
                  }}
                >
                  {selectedPeriod}
                </Typography>
                <ArrowDropDownIcon sx={{ fontSize: "1rem", opacity: 0.7 }} />
              </Box>
            )}
          </Box>
          
          {/* Right Arrow - show for all navigable periods */}
          {(selectedPeriod === "Monthly" && selectedMonth && chartData.length > 0) ? (
            <IconButton
              onClick={handleNextPoint}
              disabled={!canNavigateNext}
              size="small"
              sx={{ "&:disabled": { opacity: 0.3 } }}
            >
              <ChevronRightIcon />
            </IconButton>
          ) : (selectedPeriod && selectedPeriod !== "All time" && selectedPeriod !== "Custom" && fromDate && toDate && (selectedPeriod === "Daily" || selectedPeriod === "Weekly" || selectedPeriod === "Monthly" || selectedPeriod === "Yearly" || selectedPeriod.match(/^\d+\s*months?$/i))) ? (
            <IconButton onClick={() => handlePeriodNavigationCustom("right")} size="small" sx={{ "&:disabled": { opacity: 0.3 } }}>
              <ChevronRightIcon />
            </IconButton>
          ) : null}
          
          {/* Period Selection Menu */}
          <Menu
            anchorEl={periodMenuAnchor}
            open={Boolean(periodMenuAnchor)}
            onClose={() => setPeriodMenuAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <MenuItem onClick={() => { handlePeriodChangeCustom("Daily"); setPeriodMenuAnchor(null); }}>
              <ListItemText primary="Daily" />
            </MenuItem>
            <MenuItem onClick={() => { handlePeriodChangeCustom("Weekly"); setPeriodMenuAnchor(null); }}>
              <ListItemText primary="Weekly" />
            </MenuItem>
            <MenuItem onClick={() => { handlePeriodChangeCustom("Monthly"); setPeriodMenuAnchor(null); }}>
              <ListItemText primary="Monthly" />
            </MenuItem>
            <MenuItem onClick={() => { handlePeriodChangeCustom("Yearly"); setPeriodMenuAnchor(null); }}>
              <ListItemText primary="Yearly" />
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Scrollable Chart and Category List */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", userSelect: "none" }}>
        <Paper 
          elevation={1} 
          sx={{ 
            p: { xs: 1.5, md: 3 }, 
            mb: 2, 
            borderTopLeftRadius: 0, 
            borderTopRightRadius: 0, 
            display: "flex", 
            flexDirection: "column", 
            userSelect: "none", 
            position: "relative" 
          }}
        >
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Line Chart - Different behavior for mobile vs desktop */}
            {isMobile ? (
              // Mobile: Horizontally scrollable chart
              <Box 
                ref={chartScrollRef}
                sx={{ 
                  width: "100%", 
                  height: "280px", 
                  minHeight: "280px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  position: "relative",
                  pointerEvents: "auto",
                  WebkitOverflowScrolling: "touch",
                  scrollBehavior: "smooth",
                  touchAction: "pan-x",
                  "& svg": {
                    pointerEvents: "auto",
                  },
                  "& *": {
                    pointerEvents: "auto",
                  },
                }}
              >
                {chartData.length > 0 && animatedChartData.length > 0 ? (
                  <>
                    <Box
                    key={`chart-container-${selectedPeriod}-${fromDate}-${toDate}`}
                    sx={{
                      width: typeof chartWidth === "number" ? `${chartWidth}px` : chartWidth,
                      minWidth: "100%",
                      height: "100%",
                      minHeight: 280,
                      pointerEvents: "auto",
                      position: "relative",
                    }}
                  >
                      <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={animatedChartData} 
                      margin={{ top: 35, right: 30, left: 0, bottom: 25 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={chartDividerColor} />
                      <XAxis dataKey="date" tick={{ fontSize: "0.75rem", fill: chartTextColor }} />
                      <YAxis hide={true} domain={[0, 'auto']} />
                      <Line 
                        key={`line-${selectedMonth || 'none'}-${readyToShowChart}-${shouldAnimate}`}
                        type="monotone" 
                        dataKey="amount" 
                        stroke={transactionType === "incomes" ? "#22C55E" : "#EF4444"}
                        strokeWidth={2}
                        dot={renderDot}
                        name=""
                        isAnimationActive={shouldAnimate && readyToShowChart && !hasAnimatedOnMountRef.current}
                        animationBegin={0}
                        animationDuration={1200}
                        animationEasing="ease-in-out"
                      >
                        <LabelList 
                          dataKey="amount" 
                          position="top"
                          offset={10}
                          formatter={(value) => formatNumber(typeof value === 'number' ? value : 0)}
                          style={{ fontSize: "0.75rem", fill: chartTextColor }}
                        />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                  {/* Year labels positioned above first month of each year */}
                {(selectedPeriod === "Monthly" || selectedPeriod === "Yearly") && chartData.some(entry => entry.year) && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 5,
                          left: 0,
                          width: typeof chartWidth === "number" ? `${chartWidth}px` : chartWidth,
                          height: 20,
                          pointerEvents: "none",
                        }}
                      >
                        {chartData.map((entry, index) => {
                          if (entry.year && (index === 0 || chartData[index - 1]?.year !== entry.year)) {
                            // Calculate position: each data point is evenly spaced across chart width
                            // Use calculated chartWidth (not container width) for proper positioning
                            const actualChartWidth = typeof chartWidth === "number" ? chartWidth : (chartScrollRef.current?.clientWidth || 0);
                            const pointSpacing = chartData.length > 0 ? actualChartWidth / chartData.length : 0;
                            const xPosition = (index * pointSpacing) + (pointSpacing / 2);
                        return (
                          <Typography
                            key={`year-label-${entry.year}-${index}`}
                            variant="caption"
                            sx={{
                              position: "absolute",
                              left: `${xPosition}px`,
                              transform: "translateX(-50%)",
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              fontWeight: "medium",
                            }}
                          >
                            {entry.year}
                          </Typography>
                        );
                      }
                      return null;
                    })}
                  </Box>
                )}
                  {/* Currency Symbol Display - Bottom of graph */}
                  {getCurrencySymbol() && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                        {getCurrencySymbol()}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <Typography variant="body2" color="text.secondary">
                    No data available for the selected period
                  </Typography>
                </Box>
              )}
              </Box>
            ) : (
              // Desktop: Full-width chart, scrollable if needed
              <Box 
                ref={chartScrollRef}
                sx={{ 
                  width: "100%",
                  height: "400px", 
                  minHeight: "400px",
                  overflowX: chartData.length > 8 ? "auto" : "hidden", // Only scroll if many data points
                  overflowY: "hidden",
                  position: "relative",
                  pointerEvents: "auto",
                  WebkitOverflowScrolling: "touch",
                  scrollBehavior: "smooth",
                  "& svg": {
                    pointerEvents: "auto",
                  },
                  "& *": {
                    pointerEvents: "auto",
                  },
                }}
              >
                {chartData.length > 0 && animatedChartData.length > 0 ? (
                  <>
                    <Box
                      key={`chart-container-${selectedPeriod}-${fromDate}-${toDate}`}
                      sx={{
                        width: "100%",
                        height: "100%",
                        minHeight: 400,
                        pointerEvents: "auto",
                      }}
                    >
                      {/* Year labels positioned above first month of each year */}
                      {(selectedPeriod === "Monthly" || selectedPeriod === "Yearly") && chartData.some(entry => entry.year) && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 5,
                            left: 0,
                            width: "100%",
                            height: 20,
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          {chartData.map((entry, index) => {
                            if (entry.year && (index === 0 || chartData[index - 1]?.year !== entry.year)) {
                              const actualChartWidth = chartScrollRef.current?.clientWidth || 0;
                              const pointSpacing = chartData.length > 0 ? actualChartWidth / chartData.length : 0;
                              const xPosition = (index * pointSpacing) + (pointSpacing / 2);
                              return (
                                <Typography
                                  key={`year-label-${entry.year}-${index}`}
                                  variant="caption"
                                  sx={{
                                    position: "absolute",
                                    left: `${xPosition}px`,
                                    transform: "translateX(-50%)",
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                    fontWeight: "medium",
                                  }}
                                >
                                  {entry.year}
                                </Typography>
                              );
                            }
                            return null;
                          })}
                        </Box>
                      )}
<ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={animatedChartData}
                          margin={{ top: 35, right: 30, left: 0, bottom: 25 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={chartDividerColor} />
                          <XAxis dataKey="date" tick={{ fontSize: "0.75rem", fill: chartTextColor }} />
                          <YAxis hide={true} domain={[0, 'auto']} />
                          <Line 
                            key={`line-${selectedMonth || 'none'}-${readyToShowChart}-${shouldAnimate}`}
                            type="monotone" 
                            dataKey="amount" 
                            stroke={transactionType === "incomes" ? "#22C55E" : "#EF4444"}
                            strokeWidth={2}
                            dot={renderDot}
                            name=""
                            isAnimationActive={shouldAnimate && readyToShowChart && !hasAnimatedOnMountRef.current}
                            animationBegin={0}
                            animationDuration={1200}
                            animationEasing="ease-in-out"
                          >
                            <LabelList 
                              dataKey="amount" 
                              position="top"
                              offset={10}
                              formatter={(value) => formatNumber(typeof value === 'number' ? value : 0)}
                              style={{ fontSize: "0.75rem", fill: chartTextColor }}
                            />
                          </Line>
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    {/* Currency Symbol Display - Bottom of graph */}
                    {getCurrencySymbol() && (
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          {getCurrencySymbol()}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available for the selected period
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Sub-category Breakdown List - Below chart for both mobile and desktop */}
            {subCategoryData.length > 0 && (
              <Box>
                <AnalyticsCategoryList
                  key={`subcategory-${type}-${decodedName}-${fromDate}-${toDate}`}
                  selectedTab={transactionType || "expenses"}
                  isShowingPlaceholder={false}
                  categoryData={subCategoryData}
                  totalAmount={totalAmount}
                  formatAmount={formatAmount}
                  getCategoryColor={getCategoryColor}
                  placeholderItems={[]}
                  accounts={accounts}
                  selectedAccounts={[]}
                  selectedUsers={[]}
                  workspaceMembersLength={workspaceMembers.length}
                  fromDate={fromDate}
                  toDate={toDate}
                  aggregationMode="category"
                  onNavigateToActivity={onNavigateToActivity}
                />
              </Box>
            )}
            

            {/* Transaction List for Selected Month - Matching Activity page card structure */}
            {/* Only show transaction list for categories and accounts, not for groups */}
            {selectedMonth && selectedPeriod === "Monthly" && type !== "group" && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1.5 }}>
                  Transactions for {(() => {
                    const [year, month] = selectedMonth.split("-").map(Number);
                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                    return `${monthNames[month - 1]} ${year}`;
                  })()}
                </Typography>
                {selectedMonthTransactions.length > 0 ? (
                  <Stack spacing={0.5}>
                    {selectedMonthTransactions.map((transaction, index) => {
                      // Find related transactions (transfer fees and expense fees)
                      const isTransferFeeTransaction = transaction.type === "expense" &&
                        ((transaction.title || "") === "Transfer Fee" || (transaction.category || "") === "Transfer Fee") &&
                        transaction.transferFeeFor;
                      const isExpenseFeeTransaction = transaction.type === "expense" &&
                        ((transaction.title || "") === "Expense Fee" || (transaction.category || "") === "Expense Fee") &&
                        (transaction as any).expenseFeeFor;
                      const isFeeTransaction = isTransferFeeTransaction || isExpenseFeeTransaction;

                      const relatedTransfer = isTransferFeeTransaction && transaction.transferFeeFor
                        ? transactions.find(t => t.id === transaction.transferFeeFor && t.type === "transfer")
                        : null;
                      const relatedExpense = isExpenseFeeTransaction && (transaction as any).expenseFeeFor
                        ? transactions.find(t => t.id === (transaction as any).expenseFeeFor && (t.type === "expense" || t.type === "income"))
                        : null;

                      const relatedFee = transaction.type === "transfer"
                        ? transactions.find(t =>
                            t.type === "expense" &&
                            ((t.title || "") === "Transfer Fee" || (t.category || "") === "Transfer Fee") &&
                            t.transferFeeFor === transaction.id
                          )
                        : transaction.type === "expense" && !isFeeTransaction
                          ? transactions.find(t =>
                              t.type === "expense" &&
                              ((t.title || "") === "Expense Fee" || (t.category || "") === "Expense Fee") &&
                              (t as any).expenseFeeFor === transaction.id
                            )
                          : null;

                      // Type not shown on left – income/expense implied by amount color; adjustment shown under amount

                      const getDescription = () => {
                        if (transaction.type === "adjustment") {
                          if (transaction.isInitialBalance) {
                            return "Initial Balance";
                          } else {
                            const account = getAccountById(transaction.accountId);
                            const accountCurrency = account?.currency || defaultCurrency;
                            const adjustmentAmount = transaction.amount || 0;
                            const previousAmount = transaction.previousAmount;
                            
                            if (previousAmount !== undefined && previousAmount !== null) {
                              const newAmount = previousAmount + adjustmentAmount;
                              const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                              const formattedPrevious = formatCurrency(previousAmount, accountCurrency, accountDecimalPlaces);
                              const formattedCurrent = formatCurrency(newAmount, accountCurrency, accountDecimalPlaces);
                              return `Balance changed from ${formattedPrevious} to ${formattedCurrent}`;
                            } else {
                              const formattedAmount = formatCurrency(adjustmentAmount, accountCurrency);
                              const direction = adjustmentAmount >= 0 ? "increased" : "decreased";
                              return `Balance ${direction} to ${formattedAmount}`;
                            }
                          }
                        } else if (transaction.type === "transfer") {
                          return transaction.note || "-";
                        } else {
                          return transaction.note || "";
                        }
                      };

                      const getCategoryDisplay = () => {
                        if (transaction.type === "transfer" || transaction.type === "adjustment") {
                          return "-";
                        }
                        return transaction.category || "-";
                      };

                      const getAmountDisplay = () => {
                        if (transaction.type === "adjustment") {
                          const absAmount = Math.abs(transaction.amount || 0);
                          const sign = transaction.amount >= 0 ? "+" : "-";
                          const adjustmentDecimalPlaces = transaction.currency
                            ? getCurrencyDecimalPlaces(transaction.currency, currencies, defaultCurrency)
                            : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                          const formatted = transaction.currency
                            ? formatCurrency(absAmount, transaction.currency, adjustmentDecimalPlaces)
                            : (() => {
                                const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                                return formatCurrency(absAmount, defaultCurrency, defaultDecimalPlaces);
                              })();
                          return (
                            <Typography variant="body1" fontWeight="bold" color={transaction.amount >= 0 ? "success.main" : "error.main"} sx={{ fontSize: "0.9rem" }}>
                              {sign}{formatted}
                            </Typography>
                          );
                        } else if (transaction.type === "transfer") {
                          const fromAccount = getAccountById(transaction.fromAccountId);
                          const toAccount = getAccountById(transaction.toAccountId);
                          const currenciesDiffer = fromAccount && toAccount && fromAccount.currency !== toAccount.currency;
                          
                          if (currenciesDiffer && transaction.transferRate) {
                            const convertedAmount = (transaction.amount || 0) * transaction.transferRate;
                            const fromDecimalPlaces = getCurrencyDecimalPlaces(fromAccount?.currency || "", currencies, defaultCurrency);
                            const toDecimalPlaces = getCurrencyDecimalPlaces(toAccount?.currency || "", currencies, defaultCurrency);
                            return (
                              <Box>
                                <Typography variant="body1" fontWeight="bold" color="text.primary" sx={{ fontSize: "0.9rem" }}>
                                  {formatCurrency(transaction.amount || 0, fromAccount?.currency || "", fromDecimalPlaces)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                  {formatCurrency(convertedAmount, toAccount?.currency || "", toDecimalPlaces)}
                                </Typography>
                              </Box>
                            );
                          }
                          
                          const transferCurrency = transaction.currency || defaultCurrency;
                          const transferDecimalPlaces = getCurrencyDecimalPlaces(transferCurrency, currencies, defaultCurrency);
                          return (
                            <Typography variant="body1" fontWeight="bold" color="text.primary" sx={{ fontSize: "0.9rem" }}>
                              {transaction.currency
                                ? formatCurrency(transaction.amount || 0, transaction.currency, transferDecimalPlaces)
                                : (() => {
                                    const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                                    return formatCurrency(transaction.amount || 0, defaultCurrency, defaultDecimalPlaces);
                                  })()}
                            </Typography>
                          );
                        } else {
                          const transactionCurrency = transaction.currency || defaultCurrency;
                          const currenciesDiffer = transactionCurrency !== defaultCurrency;
                          
                          const transactionDecimalPlaces = transaction.currency 
                            ? getCurrencyDecimalPlaces(transaction.currency, currencies, defaultCurrency)
                            : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                          const formatted = transaction.currency
                            ? formatCurrency(transaction.amount || 0, transaction.currency, transactionDecimalPlaces)
                            : (() => {
                                const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                                return formatCurrency(transaction.amount || 0, defaultCurrency, defaultDecimalPlaces);
                              })();
                          
                          if (currenciesDiffer && transaction.amount) {
                            let amountInDefaultCurrency: number;
                            const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                            
                            if (transaction.amountInDefaultCurrency !== undefined && 
                                transaction.defaultCurrencyAtTime === defaultCurrency) {
                              amountInDefaultCurrency = transaction.amountInDefaultCurrency;
                            } else if (transaction.amountInDefaultCurrency !== undefined && 
                                       transaction.defaultCurrencyAtTime) {
                              const oldDefaultCurrency = transaction.defaultCurrencyAtTime;
                              const conversionRate = calculateTransferRate(oldDefaultCurrency, defaultCurrency);
                              amountInDefaultCurrency = transaction.amountInDefaultCurrency * conversionRate;
                            } else {
                              const rateToDefault = calculateTransferRate(transactionCurrency, defaultCurrency);
                              amountInDefaultCurrency = (transaction.amount || 0) * rateToDefault;
                            }
                            
                            return (
                              <Box>
                                <Typography variant="body1" fontWeight="bold" color={transaction.isIncome ? "success.main" : "error.main"} sx={{ fontSize: "0.9rem" }}>
                                  {transaction.isIncome ? "+" : "-"}{formatted}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                  {formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}
                                </Typography>
                              </Box>
                            );
                          }
                          
                          return (
                            <Typography variant="body1" fontWeight="bold" color={transaction.isIncome ? "success.main" : "error.main"} sx={{ fontSize: "0.9rem" }}>
                              {transaction.isIncome ? "+" : "-"}{formatted}
                            </Typography>
                          );
                        }
                      };

                      return (
                        <Box key={`${transaction.id}-${index}`} data-transaction-id={transaction.id} sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", mb: 0, pb: 0 }}>
                          <Card 
                            elevation={1}
                            data-card-id={transaction.id}
                            onClick={
                              isProtectedAdjustment(transaction)
                                ? (e) => {
                                    if ((e.target as HTMLElement).closest("button") || (e.target as HTMLElement).closest("[role='button']")) return;
                                    showAdjustmentNotEditable();
                                  }
                                : undefined
                            }
                            sx={{
                              width: "100%",
                              border: "none",
                              borderRadius: 1.5,
                              overflow: "hidden",
                              bgcolor: "background.paper",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                              ...(isProtectedAdjustment(transaction)
                                ? {
                                    opacity: 0.85,
                                    cursor: "default",
                                    "&:hover": {},
                                  }
                                : {
                                    cursor: "default",
                                    "&:hover": { 
                                      bgcolor: "action.hover",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                                      transform: "translateY(-1px)",
                                    },
                                  }),
                              pointerEvents: "auto",
                              transition: "all 0.2s ease-in-out, background-color 0.5s ease-in-out",
                            }}
                          >
                            <CardContent 
                              sx={{ 
                                pt: "6px", 
                                px: "8px", 
                                pb: "2px !important",
                                "&.MuiCardContent-root": {
                                  paddingBottom: "2px !important",
                                },
                                "&:last-child": { 
                                  pb: "2px !important",
                                  paddingBottom: "2px !important",
                                }
                              }}
                            >
                              {/* Header: Date, User initial, Category | Amount, Adjustment */}
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.15 }}>
                                <Stack spacing={0.15} sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                      {formatDate(transaction.date)}
                                    </Typography>
                                    {formatTime(transaction.date) !== "HH:MM" && formatTime(transaction.date) !== "HH:MM AM" && (
                                      <>
                                        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7, fontSize: "0.65rem" }}>
                                          {formatTime(transaction.date)}
                                        </Typography>
                                      </>
                                    )}
                                    {/* Connection indicators */}
                                    {isFeeTransaction && relatedTransfer && (
                                      <Tooltip title={`Fee for transfer: ${formatCurrency(relatedTransfer.amount || 0, relatedTransfer.currency || defaultCurrency)}`}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const transferElement = document.querySelector(`[data-transaction-id="${relatedTransfer.id}"]`) as HTMLElement;
                                            if (transferElement) {
                                              transferElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                              setTimeout(() => {
                                                let cardElement = transferElement.querySelector(`[data-card-id="${relatedTransfer.id}"]`) as HTMLElement;
                                                if (!cardElement && transferElement.firstElementChild) {
                                                  cardElement = transferElement.firstElementChild as HTMLElement;
                                                }
                                                if (!cardElement) {
                                                  cardElement = transferElement.querySelector("div[class*='MuiCard-root']") as HTMLElement;
                                                }
                                                if (cardElement) {
                                                  cardElement.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                                  cardElement.style.transition = "background-color 0.5s ease-in-out";
                                                  const cardContent = cardElement.querySelector(".MuiCardContent-root") as HTMLElement;
                                                  if (cardContent) {
                                                    cardContent.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                                    cardContent.style.transition = "background-color 0.5s ease-in-out";
                                                  }
                                                  setTimeout(() => {
                                                    cardElement.style.transition = "background-color 1s ease-in-out";
                                                    cardElement.style.setProperty("background-color", "", "important");
                                                    if (cardContent) {
                                                      cardContent.style.transition = "background-color 1s ease-in-out";
                                                      cardContent.style.setProperty("background-color", "", "important");
                                                    }
                                                  }, 2500);
                                                }
                                              }, 100);
                                            }
                                          }}
                                          sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                        >
                                          <LinkIcon sx={{ fontSize: 12, color: "primary.main" }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {isFeeTransaction && relatedExpense && (
                                      <Tooltip title={`Fee for expense: ${(relatedExpense.title || relatedExpense.category || "").slice(0, 30)}`}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const expenseElement = document.querySelector(`[data-transaction-id="${relatedExpense.id}"]`) as HTMLElement;
                                            if (expenseElement) {
                                              expenseElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                              setTimeout(() => {
                                                let cardElement = expenseElement.querySelector(`[data-card-id="${relatedExpense.id}"]`) as HTMLElement;
                                                if (!cardElement && expenseElement.firstElementChild) cardElement = expenseElement.firstElementChild as HTMLElement;
                                                if (!cardElement) cardElement = expenseElement.querySelector("div[class*='MuiCard-root']") as HTMLElement;
                                                if (cardElement) {
                                                  cardElement.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                                  cardElement.style.transition = "background-color 0.5s ease-in-out";
                                                  setTimeout(() => {
                                                    cardElement.style.transition = "background-color 1s ease-in-out";
                                                    cardElement.style.setProperty("background-color", "", "important");
                                                  }, 2500);
                                                }
                                              }, 100);
                                            }
                                          }}
                                          sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                        >
                                          <LinkIcon sx={{ fontSize: 12, color: "primary.main" }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {(transaction.type === "transfer" || transaction.type === "expense") && relatedFee && (
                                      <Tooltip title={`Has fee: ${formatCurrency(relatedFee.amount || 0, relatedFee.currency || defaultCurrency)}`}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const feeElement = document.querySelector(`[data-transaction-id="${relatedFee.id}"]`) as HTMLElement;
                                            if (feeElement) {
                                              feeElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                              setTimeout(() => {
                                                let cardElement = feeElement.querySelector(`[data-card-id="${relatedFee.id}"]`) as HTMLElement;
                                                if (!cardElement && feeElement.firstElementChild) {
                                                  cardElement = feeElement.firstElementChild as HTMLElement;
                                                }
                                                if (!cardElement) {
                                                  cardElement = feeElement.querySelector("div[class*='MuiCard-root']") as HTMLElement;
                                                }
                                                if (cardElement) {
                                                  cardElement.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                                  cardElement.style.transition = "background-color 0.5s ease-in-out";
                                                  const cardContent = cardElement.querySelector(".MuiCardContent-root") as HTMLElement;
                                                  if (cardContent) {
                                                    cardContent.style.setProperty("background-color", "rgba(144, 202, 249, 0.4)", "important");
                                                    cardContent.style.transition = "background-color 0.5s ease-in-out";
                                                  }
                                                  setTimeout(() => {
                                                    cardElement.style.transition = "background-color 1s ease-in-out";
                                                    cardElement.style.setProperty("background-color", "", "important");
                                                    if (cardContent) {
                                                      cardContent.style.transition = "background-color 1s ease-in-out";
                                                      cardContent.style.setProperty("background-color", "", "important");
                                                    }
                                                  }, 2500);
                                                }
                                              }, 100);
                                            }
                                          }}
                                          sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                        >
                                          <AccountBalanceWalletIcon sx={{ fontSize: 12, color: "warning.main" }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {transaction.userId && workspaceMembers.length > 1 && (
                                      <Tooltip title={`Added by ${getUserName(transaction.userId)}`}>
                                        <Avatar
                                          src={getUserPhotoURL(transaction.userId) || undefined}
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            bgcolor: "primary.main",
                                            fontSize: "0.5rem",
                                          }}
                                        >
                                          {getUserName(transaction.userId)[0]?.toUpperCase() || "?"}
                                        </Avatar>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  {(getCategoryDisplay() !== "-" || transaction.type === "adjustment") && (
                                    transaction.type === "adjustment" ? (
                                      <Tooltip title={expandedAdjustmentId === transaction.id ? "Tap to collapse" : "Tap to see full text"}>
                                        <Typography
                                          component="span"
                                          variant="caption"
                                          fontWeight="medium"
                                          color="text.primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedAdjustmentId((prev) => (prev === transaction.id ? null : transaction.id));
                                          }}
                                          sx={{
                                            fontSize: "0.7rem",
                                            lineHeight: 1.2,
                                            minWidth: 0,
                                            cursor: "pointer",
                                            ...(expandedAdjustmentId === transaction.id
                                              ? { wordBreak: "break-word" }
                                              : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                                          }}
                                        >
                                          {getDescription()}
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      <Typography variant="caption" fontWeight="medium" color="text.primary" sx={{ fontSize: "0.7rem", lineHeight: 1.2, wordBreak: "break-word" }}>
                                        {getCategoryDisplay()}
                                      </Typography>
                                    )
                                  )}
                                </Stack>
                                <Stack spacing={0.15} alignItems="flex-end" sx={{ ml: 0.5, flexShrink: 0, minWidth: "5rem", "& > *": { textAlign: "right" } }}>
                                  <Box sx={{ width: "100%" }}>{getAmountDisplay()}</Box>
                                </Stack>
                              </Stack>

                              {/* Description/Title – only for non-adjustment so adjustment cards stay same height */}
                              {getDescription() && transaction.type !== "adjustment" && (
                                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.15, wordBreak: "break-word", fontSize: "0.75rem", lineHeight: 1.25 }}>
                                  {getDescription()}
                                </Typography>
                              )}

                              {/* Account line with expand and menu buttons */}
                              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ justifyContent: "space-between", mb: 0.05 }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}>
                                      {(() => {
                                        if (transaction.type === "transfer") {
                                          return `${getAccountName(transaction.fromAccountId, transaction.fromAccountName)} → ${getAccountName(transaction.toAccountId, transaction.toAccountName)}`;
                                        } else {
                                          return getAccountName(transaction.accountId, transaction.accountName);
                                        }
                                      })()}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" alignItems="center" spacing={0.25}>
                                    {/* Expand arrow for details - always clickable so user can expand/collapse; details show when present */}
                                    <Tooltip title={expandedDetails.has(transaction.id) ? "Hide details" : (transaction.details && Object.keys(transaction.details).length > 0 ? "Show details" : "Show details")}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedDetails((prev) => {
                                              const newSet = new Set(prev);
                                              if (newSet.has(transaction.id)) {
                                                newSet.delete(transaction.id);
                                              } else {
                                                newSet.add(transaction.id);
                                              }
                                              return newSet;
                                            });
                                          }}
                                          sx={{ p: 0.25 }}
                                        >
                                          {expandedDetails.has(transaction.id) ? (
                                            <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                          ) : (
                                            <ChevronRightIcon sx={{ fontSize: 14 }} />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {/* Menu button */}
                                    <Tooltip
                                      title={
                                        isProtectedAdjustment(transaction)
                                          ? "This adjustment transaction cannot be edited"
                                          : usesArchivedAccount(transaction)
                                          ? `The account "${getArchivedAccountNames(transaction).join('", "')}" used for this transaction is currently archived, so this transaction cannot be edited.`
                                          : ""
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMenuOpen(e, transaction);
                                        }}
                                        disabled={isProtectedAdjustment(transaction) || usesArchivedAccount(transaction)}
                                        sx={{ p: 0.25, zIndex: 1, position: "relative" }}
                                      >
                                        <MoreVertIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                              </Stack>

                              {/* Transaction Details - Expandable; show when expanded, "No details" if empty */}
                              {expandedDetails.has(transaction.id) && (
                                <Box sx={{ mt: 0.25, pt: 0.25, pb: 0.25, bgcolor: "action.hover", borderRadius: 0.75, px: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.15, display: "block", fontSize: "0.65rem" }}>
                                    Details:
                                  </Typography>
                                  {transaction.details && Object.keys(transaction.details).length > 0 ? (
                                    <Stack spacing={0.15}>
                                      {getDetailOrder(transaction).map((key) => {
                                        const value = transaction.details![key];
                                        if (value === undefined) return null;
                                        const unit = getDetailUnit(transaction, key);
                                        const displayValue = typeof value === "number" ? value.toString() : value || "-";
                                        return (
                                          <Stack key={key} direction="row" spacing={0.5} alignItems="flex-start">
                                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, fontSize: "0.65rem" }}>
                                              {key}:
                                            </Typography>
                                            <Typography variant="caption" sx={{ flex: 1, fontSize: "0.65rem" }}>
                                              {displayValue}{unit ? ` ${unit}` : ""}
                                            </Typography>
                                          </Stack>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                      No details for this transaction.
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* Attachments: legacy + custom field attachments (same as Activity transaction list) */}
                              {getTransactionAttachments(transaction).length > 0 && (
                                <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mt: 0.15, mb: 0 }} gap={0.5}>
                                  {getTransactionAttachments(transaction).map((att, i) => (
                                    <Tooltip key={`${att.url}-${i}`} title={att.name || "View attachment"}>
                                      <IconButton
                                        size="small"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const { url: signedUrl, expiresIn } = await api.getAttachmentSignedUrl(workspaceId, att.url);
                                            setPreviewUrl(signedUrl);
                                            setPreviewName(att.name || "Attachment");
                                            setPreviewType(att.type || null);
                                            setPreviewExpiresAt(Date.now() + expiresIn * 1000);
                                            setPreviewOpen(true);
                                          } catch (err) {
                                            setAttachmentErrorSnackbar(err instanceof Error ? err.message : "Could not open attachment");
                                          }
                                        }}
                                        sx={{ p: 0.25 }}
                                        color="primary"
                                      >
                                        {getAttachmentIcon(att.type, att.name)}
                                      </IconButton>
                                    </Tooltip>
                                  ))}
                                </Stack>
                              )}
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ py: 3, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      No transactions found for {(() => {
                        const [year, month] = selectedMonth.split("-").map(Number);
                        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                        return `${monthNames[month - 1]} ${year}`;
                      })()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, opacity: 0.8 }}>
                      Total: {formatAmount(0)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Transaction Menu */}
      <TransactionMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        selectedTransaction={selectedTransaction}
        canViewTransactionHistory={canViewTransactionHistory}
        canDeleteTransactions={canDeleteTransactions}
        canEditTransactions={canEditTransactions}
        isProtectedAdjustment={isProtectedAdjustment}
        usesArchivedAccount={usesArchivedAccount}
        getArchivedAccountNames={getArchivedAccountNames}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onCopyUrl={handleCopyUrl}
        onHistory={handleHistory}
        onDelete={handleDelete}
      />

      {/* Centered "Adjustment not editable" message (fades out after ~1s) */}
      {showAdjustmentMessage && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1300}
          sx={{
            pointerEvents: "none",
            opacity: adjustmentMessageVisible ? 1 : 0,
            transition: "opacity 0.3s ease-out",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              bgcolor: "background.paper",
              color: "text.primary",
              px: 2,
              py: 1.5,
              borderRadius: 1,
              boxShadow: 2,
            }}
          >
            Adjustment not editable
          </Typography>
        </Box>
      )}

      {/* Transaction History Dialog */}
      <TransactionHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        transactionHistory={transactionHistory}
        loadingHistory={loadingHistory}
        selectedTransaction={selectedTransaction}
        workspaceId={workspaceId}
        getUserNameForHistory={getUserNameForHistory}
        getUserName={getUserName}
        getAttachmentIcon={getAttachmentIcon}
        formatDate={formatDate}
        formatTime={formatTime}
        transactionCustomFields={transactionCustomFields}
        onAttachmentError={(msg) => setAttachmentErrorSnackbar(msg)}
      />

      {/* Attachment Preview Dialog */}
      <AttachmentPreviewDialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewExpiresAt(null);
        }}
        previewUrl={previewUrl}
        previewName={previewName}
        previewType={previewType}
        previewExpiresAt={previewExpiresAt}
      />

      {/* Attachment error snackbar */}
      <Snackbar
        open={Boolean(attachmentErrorSnackbar)}
        autoHideDuration={6000}
        onClose={() => setAttachmentErrorSnackbar(null)}
        message={attachmentErrorSnackbar ?? undefined}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Desktop: edit transaction in a popup on this page (no navigation to Activity) */}
      <TransactionEditDialogStandalone
        workspaceId={workspaceId}
        workspaceOwnerId={workspaceOwnerId}
        accounts={accounts}
        expenseCategories={expenseCategories}
        incomeCategories={incomeCategories}
        categoryGroups={groups}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        transactions={transactions}
        transactionCustomFields={transactionCustomFields}
        open={editDialogOpen}
        transactionToEdit={transactionToEdit}
        onClose={() => {
          setEditDialogOpen(false);
          setTransactionToEdit(null);
        }}
        onSaved={() => {
          // Transactions list will refetch via useAnalyticsTransactions when dialog closes
        }}
      />
    </Box>
  );
}

export default CategoryGroupDetail;
